import Service from '@ember/service';
import { autoGenerateLabel, stripTypeSuffixes, getFieldGroup } from '../utils/label-generation';

/**
 * FieldConfig Service - Central authority for all field-related operations
 *
 * SINGLE SOURCE OF TRUTH for:
 * - Field name format conversions (snake_case ↔ camelCase)
 * - Field metadata (type, group, capabilities)
 * - Label generation and normalization
 * - Column mappings and data access patterns
 *
 * DATA FLOW FORMATS:
 * 1. OpenSearch Index: snake_case (e.g., 'employee_name', 'payroll_id')
 * 2. Rails API Response: camelCase (e.g., 'employeeName', 'payrollId')
 * 3. Query Parameters: snake_case for backward compatibility
 * 4. UI Display: Human-readable labels (e.g., 'Employee Name', 'Payroll ID')
 *
 * FIELD CAPABILITIES:
 * - searchable: Can be used in OpenSearch queries
 * - filterable: Can be used in filter UI
 * - groupable: Can be used in aggregations/grouping
 * - sortable: Can be used for sorting results
 */

export interface FieldMetadata {
  // Core identifiers
  opensearchKey: string; // snake_case - How it appears in OpenSearch (e.g., 'employee_name')
  apiKey: string; // camelCase - How it appears in API responses (e.g., 'employeeName')
  queryParamKey: string; // snake_case - How it appears in URL query params (e.g., 'employee_name')

  // Display
  label: string; // Human-readable label (e.g., 'Employee Name')
  shortLabel?: string; // Abbreviated label for tight spaces
  group?: string; // Category for grouping in UI (e.g., 'Employee', 'Program', 'Application')

  // Field type
  type: 'keyword' | 'text' | 'date' | 'boolean' | 'long' | 'integer' | 'object' | 'nested';
  isDate?: boolean;
  isTimestamp?: boolean;

  // Capabilities
  searchable: boolean; // Can be used in OpenSearch queries
  filterable: boolean; // Can be used in filter UI
  groupable: boolean; // Can be used in aggregations/grouping
  sortable: boolean; // Can be used for sorting

  // Data access
  dataPath?: string; // Alternative path for nested data (e.g., 'employee.name')
}

export interface FieldGroup {
  key: string;
  label: string;
  fields: string[]; // List of field opensearchKeys in this group
}

export default class FieldConfigService extends Service {
  // ============================================================================
  // FORMAT CONVERSION - The definitive conversion logic
  // ============================================================================

  /**
   * Convert snake_case to camelCase
   * Example: 'employee_name_keyword' → 'employeeNameKeyword'
   */
  snakeToCamel(str: string): string {
    if (!str.includes('_')) return str; // Already camelCase
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
  }

  /**
   * Convert camelCase to snake_case
   * Example: 'employeeNameKeyword' → 'employee_name_keyword'
   */
  camelToSnake(str: string): string {
    if (str.includes('_')) return str; // Already snake_case
    return str.replace(/([A-Z])/g, '_$1').toLowerCase();
  }

  /**
   * Normalize field key for comparison (removes underscores, dots, lowercases)
   * Use this to check if two keys refer to the same field regardless of format
   * Example: 'employeeName' and 'employee_name' both normalize to 'employeename'
   */
  normalizeKey(str: string): string {
    return str.replace(/[_.]/g, '').toLowerCase();
  }

  /**
   * Check if two field keys refer to the same field
   */
  keysMatch(key1: string, key2: string): boolean {
    return this.normalizeKey(key1) === this.normalizeKey(key2);
  }

  // ============================================================================
  // LABEL GENERATION - Consistent label formatting
  // ============================================================================

  /**
   * Generate a human-readable label from a field key.
   * Delegates to shared label-generation.ts for consistent formatting.
   *
   * @param fieldKey - Field key in any format
   * @param options - Formatting options
   * @returns Formatted label
   */
  formatLabel(
    fieldKey: string,
    options: {
      stripPrefixes?: boolean;
      stripSuffixes?: boolean;
      shortForm?: boolean;
    } = {}
  ): string {
    let label = fieldKey;

    // Remove .keyword suffix if present
    label = label.replace(/\.keyword$/i, '');

    // Strip technical suffixes if requested (delegates to shared utility)
    if (options.stripSuffixes) {
      label = stripTypeSuffixes(label);
      // Also strip Id suffix (semantic, not an OpenSearch type suffix)
      label = label.replace(/Id$/i, '');
    }

    // Strip common prefixes if requested
    if (options.stripPrefixes) {
      const prefixes = [
        'tasProgramTemplate',
        'tasProgramTemplateField',
        'tasProgram',
        'tasApplication',
        'employee',
        'payment',
        'course',
      ];
      for (const prefix of prefixes) {
        const regex = new RegExp(`^${prefix}`, 'i');
        label = label.replace(regex, '');
      }
    }

    // Delegate final formatting to shared autoGenerateLabel
    return autoGenerateLabel(label);
  }

  // ============================================================================
  // FIELD METADATA REGISTRY - Define all known fields
  // ============================================================================

  /**
   * Registry of known fields and their metadata
   * This is the single source of truth for field configuration
   */
  private fieldRegistry: Map<string, FieldMetadata> = new Map();

  /**
   * Register a field with its metadata
   */
  registerField(metadata: FieldMetadata) {
    this.fieldRegistry.set(metadata.opensearchKey, metadata);
  }

  /**
   * Get metadata for a field by any key format (snake_case, camelCase, etc.)
   */
  getFieldMetadata(key: string): FieldMetadata | undefined {
    // Try direct lookup first
    if (this.fieldRegistry.has(key)) {
      return this.fieldRegistry.get(key);
    }

    // Try normalized lookup - find field with matching normalized key
    const normalizedKey = this.normalizeKey(key);
    for (const [_, metadata] of this.fieldRegistry) {
      if (
        this.normalizeKey(metadata.opensearchKey) === normalizedKey ||
        this.normalizeKey(metadata.apiKey) === normalizedKey ||
        this.normalizeKey(metadata.queryParamKey) === normalizedKey
      ) {
        return metadata;
      }
    }

    return undefined;
  }

  /**
   * Get all registered fields, optionally filtered by capabilities
   */
  getFields(filter?: {
    searchable?: boolean;
    filterable?: boolean;
    groupable?: boolean;
    sortable?: boolean;
    group?: string;
  }): FieldMetadata[] {
    let fields = Array.from(this.fieldRegistry.values());

    if (filter) {
      fields = fields.filter((field) => {
        if (filter.searchable !== undefined && field.searchable !== filter.searchable) return false;
        if (filter.filterable !== undefined && field.filterable !== filter.filterable) return false;
        if (filter.groupable !== undefined && field.groupable !== filter.groupable) return false;
        if (filter.sortable !== undefined && field.sortable !== filter.sortable) return false;
        if (filter.group !== undefined && field.group !== filter.group) return false;
        return true;
      });
    }

    return fields;
  }

  // ============================================================================
  // DATA ACCESS - Convert keys and access data consistently
  // ============================================================================

  /**
   * Get the appropriate key for accessing API response data
   * Converts any input format to the camelCase format used in API responses
   *
   * @param fieldKey - Field key in any format
   * @returns camelCase key for data access
   */
  getDataAccessKey(fieldKey: string): string {
    const metadata = this.getFieldMetadata(fieldKey);
    if (metadata) {
      return metadata.apiKey;
    }

    // Fallback: assume snake_case input, convert to camelCase
    return this.snakeToCamel(fieldKey);
  }

  /**
   * Get value from data object using field key in any format
   *
   * @param data - Data object with camelCase keys (as returned from API)
   * @param fieldKey - Field key in any format
   * @returns Value from data object
   */
  getValue(data: Record<string, unknown>, fieldKey: string): unknown {
    const dataKey = this.getDataAccessKey(fieldKey);
    const metadata = this.getFieldMetadata(fieldKey);

    // If metadata specifies an alternative data path, use it
    if (metadata?.dataPath) {
      const parts = metadata.dataPath.split('.');
      let value: unknown = data;
      for (const part of parts) {
        value = (value as Record<string, unknown>)?.[part];
        if (value === undefined) break;
      }
      return value;
    }

    // Standard direct access
    return data[dataKey];
  }

  // ============================================================================
  // FIELD GROUPS - Organize fields into logical categories
  // ============================================================================

  /**
   * Get field groups for organizing UI
   */
  getFieldGroups(): FieldGroup[] {
    const groups = new Map<string, FieldGroup>();

    for (const field of this.fieldRegistry.values()) {
      if (field.group) {
        if (!groups.has(field.group)) {
          groups.set(field.group, {
            key: field.group,
            label: field.group,
            fields: [],
          });
        }
        groups.get(field.group)!.fields.push(field.opensearchKey);
      }
    }

    return Array.from(groups.values());
  }

  // ============================================================================
  // INITIALIZATION - Register default fields
  // ============================================================================

  /**
   * Register common TAS program fields
   * This should be expanded as we discover more fields
   */
  constructor(...args: ConstructorParameters<typeof Service>) {
    super(...args);
    this.registerDefaultFields();
  }

  private registerDefaultFields() {
    // NOTE: Only register fields that are actively used and confirmed to exist
    // The registerFromMapping() method will handle dynamic field registration
    // from OpenSearch mappings for all other fields.

    // Keep this minimal - only core fields that need precise metadata
    // or that serve as examples for the service

    // Employee fields
    this.registerField({
      opensearchKey: 'employee_name',
      apiKey: 'employeeName',
      queryParamKey: 'employee_name',
      label: 'Employee Name',
      group: 'Employee',
      type: 'keyword',
      searchable: true,
      filterable: true,
      groupable: true,
      sortable: true,
    });

    this.registerField({
      opensearchKey: 'employee_payroll_id_keyword',
      apiKey: 'employeePayrollIdKeyword',
      queryParamKey: 'employee_payroll_id_keyword',
      label: 'Payroll ID',
      group: 'Employee',
      type: 'keyword',
      searchable: true,
      filterable: true,
      groupable: true,
      sortable: true,
    });

    // All other fields should be registered dynamically via registerFromMapping()
    // This keeps the service flexible and avoids hardcoding deprecated fields
  }

  // ============================================================================
  // DYNAMIC FIELD REGISTRATION - From OpenSearch mappings
  // ============================================================================

  /**
   * Register fields dynamically from OpenSearch mapping
   * This supplements the manually registered default fields
   */
  registerFromMapping(
    mapping: Record<
      string,
      {
        type: string;
        fields?: Record<string, { type: string }>;
      }
    >
  ) {
    for (const [fieldName, fieldConfig] of Object.entries(mapping)) {
      // Skip if already registered
      if (this.getFieldMetadata(fieldName)) {
        continue;
      }

      // Infer capabilities from field type
      const type = fieldConfig.type as FieldMetadata['type'];
      const isKeyword = type === 'keyword';
      const isDate = type === 'date';
      const isText = type === 'text';

      // Auto-register field with sensible defaults
      this.registerField({
        opensearchKey: fieldName,
        apiKey: this.snakeToCamel(fieldName),
        queryParamKey: fieldName,
        label: this.formatLabel(fieldName, { stripSuffixes: true }),
        group: this.inferGroup(fieldName),
        type,
        isDate,
        isTimestamp: isDate || this.isTimestampField(fieldName),
        searchable: isKeyword || isText || isDate,
        filterable: isKeyword || isDate,
        groupable: isKeyword,
        sortable: isKeyword || isDate,
      });

      // Register keyword subfield if present
      if (isText && fieldConfig.fields?.keyword) {
        const keywordFieldName = `${fieldName}.keyword`;
        this.registerField({
          opensearchKey: keywordFieldName,
          apiKey: this.snakeToCamel(keywordFieldName),
          queryParamKey: keywordFieldName,
          label: this.formatLabel(fieldName, { stripSuffixes: true }), // Use base field label
          group: this.inferGroup(fieldName),
          type: 'keyword',
          searchable: true,
          filterable: true,
          groupable: true,
          sortable: true,
        });
      }
    }
  }

  /**
   * Infer group from field name.
   * Delegates to shared getFieldGroup in label-generation.ts.
   */
  private inferGroup(fieldName: string): string {
    return getFieldGroup(fieldName);
  }

  /**
   * Check if field name suggests it's a timestamp
   */
  private isTimestampField(fieldName: string): boolean {
    return /(_at|_date|_time|At|Date|Time)$/.test(fieldName);
  }
}

// Augment the service registry
declare module '@ember/service' {
  interface Registry {
    'field-config': FieldConfigService;
  }
}
